import type { CSSProperties } from 'react';
import type { Proxy, ProxyFilters, ProxyUpload } from 'services/proxy/proxyService';
import type {
  ProxyStats,
  ProxyStatusValue,
  ProxySortBy,
  ProxySortOrder,
  ProxyFormFields,
  ParsedProxy,
} from './proxies-manager.types';
import { DEFAULT_BULK_COUNTRY } from './proxies-manager.constants';

// Local minimal signature so this util does not couple to i18next's stricter TFunction
// generics. Only the (key, fallback) call shape is used here.
type TranslateFn = (key: string, fallback?: string) => string;

/**
 * Extract a human-readable message from an unknown error value.
 */
export const getErrorMessage = (error: unknown, fallback = ''): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (
    error !== null &&
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message;
  }
  return fallback;
};

/**
 * Build a ProxyFilters object from current UI filter/sort/pagination state.
 */
export const buildProxyFilters = (args: {
  currentPage: number;
  itemsPerPage: number;
  searchQuery: string;
  statusFilter: string;
  countryFilter: string;
  providerFilter: string;
  sortBy: ProxySortBy;
  sortOrder: ProxySortOrder;
}): ProxyFilters => ({
  page: args.currentPage,
  limit: args.itemsPerPage,
  search: args.searchQuery || undefined,
  status: (args.statusFilter || undefined) as ProxyStatusValue | undefined,
  country: args.countryFilter || undefined,
  provider: args.providerFilter || undefined,
  sortBy: args.sortBy,
  sortOrder: args.sortOrder,
});

/**
 * Parse a bulk-upload textarea into a list of ProxyUpload rows.
 * Each non-empty line is split on whitespace into: proxyString [country] [provider].
 */
export const parseBulkUploadText = (text: string): ProxyUpload[] => {
  const lines = text.split('\n').filter(line => line.trim());
  return lines.map(line => {
    const parts = line.trim().split(/\s+/);
    const proxyPart = parts[0];
    const country = parts[1] || DEFAULT_BULK_COUNTRY;
    const provider = parts[2] || undefined;
    return {
      proxy: proxyPart,
      country: country.toUpperCase(),
      provider,
    };
  });
};

/**
 * Count non-empty lines in the bulk-upload textarea.
 */
export const countBulkUploadLines = (text: string): number =>
  text.split('\n').filter(line => line.trim()).length;

/**
 * Compute stats from the currently-loaded page of proxies as a fallback
 * when the stats endpoint fails.
 */
export const calculateStatsFromProxies = (
  proxies: Proxy[],
  totalItems?: number
): ProxyStats => ({
  total: totalItems ?? proxies.length,
  available: proxies.filter(p => p.status === 'available').length,
  inUse: proxies.filter(p => p.status === 'in-use' || p.status === 'in_use').length,
  disabled: proxies.filter(p => p.status === 'disabled').length,
  byCountry: proxies.reduce<Record<string, number>>((acc, p) => {
    acc[p.country] = (acc[p.country] || 0) + 1;
    return acc;
  }, {}),
});

/**
 * Translate a proxy status value into a display label.
 */
export const formatStatus = (status: string, t: TranslateFn): string => {
  switch (status) {
    case 'available':
      return t('admin.proxies.status.available', 'Available');
    case 'in-use':
    case 'in_use':
      return t('admin.proxies.status.inUse', 'In Use');
    case 'assigned_to_user':
      return t('admin.proxies.status.assignedToUser', 'Assigned');
    case 'disabled':
      return t('admin.proxies.status.disabled', 'Disabled');
    default:
      return status;
  }
};

/**
 * Format a lastUsed ISO string for display, or return the "never" label when absent.
 */
export const formatLastUsed = (
  lastUsed: string | undefined,
  neverLabel: string
): string => {
  if (!lastUsed) return neverLabel;
  return new Date(lastUsed).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Convert a Proxy + its parsed address into the form fields used by the edit modal.
 */
export const proxyToFormFields = (
  proxy: Proxy,
  parsed: ParsedProxy
): ProxyFormFields => ({
  ip: parsed.ip,
  port: parsed.port.toString(),
  username: parsed.username || '',
  password: parsed.password || '',
  country: proxy.country,
  provider: proxy.provider || '',
});

/**
 * Compute the visible page-number window for the paginator.
 */
export const getPaginationRange = (
  currentPage: number,
  totalPages: number,
  maxVisiblePages: number
): { startPage: number; endPage: number } => {
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }
  return { startPage, endPage };
};

/**
 * Status badge inline style, themed by dark/light mode.
 */
export const getStatusBadgeStyle = (
  status: string,
  isDark: boolean
): CSSProperties => {
  switch (status) {
    case 'available':
      return {
        background: isDark ? '#0D2818' : '#DCFCE7',
        color: isDark ? '#5EE9B5' : '#15803D',
        borderColor: isDark ? '#166534' : '#BBF7D0',
      };
    case 'in-use':
    case 'in_use':
      return {
        background: isDark ? '#0C1A2E' : '#DBEAFE',
        color: isDark ? '#60A5FA' : '#1D4ED8',
        borderColor: isDark ? '#1E40AF' : '#BFDBFE',
      };
    case 'assigned_to_user':
      return {
        background: isDark ? '#1A0D2E' : '#F3E8FF',
        color: isDark ? '#C084FC' : '#7C3AED',
        borderColor: isDark ? '#6D28D9' : '#DDD6FE',
      };
    case 'disabled':
      return {
        background: isDark ? '#1D293D' : '#F3F4F6',
        color: isDark ? '#90A1B9' : '#475569',
        borderColor: isDark ? '#314158' : '#E5E7EB',
      };
    default:
      return {
        background: isDark ? '#1D293D' : '#F3F4F6',
        color: isDark ? '#90A1B9' : '#475569',
        borderColor: isDark ? '#314158' : '#E5E7EB',
      };
  }
};

export interface ProxiesThemeColors {
  containerBg: string;
  mainBg: string;
  cardBorder: string;
  titleColor: string;
  subtitleColor: string;
  tableHeaderBg: string;
  tableBorderColor: string;
  inputBg: string;
  inputBorder: string;
  inputText: string;
  buttonBg: string;
  buttonText: string;
  paginationBg: string;
  paginationBorder: string;
  paginationText: string;
  modalBackdrop: string;
  modalBg: string;
  modalInfoBg: string;
  countryBadge: CSSProperties;
  providerBadge: CSSProperties;
  filterChipSearch: CSSProperties;
  filterChipStatus: CSSProperties;
  filterChipCountry: CSSProperties;
  filterChipProvider: CSSProperties;
  warningBoxBg: string;
  warningBoxBorder: string;
  warningBoxTitle: string;
  warningBoxText: string;
  warningBoxIcon: string;
  infoBoxBg: string;
  infoBoxBorder: string;
  infoBoxTitle: string;
  infoBoxText: string;
  infoBoxIcon: string;
  infoBoxCodeBg: string;
  infoBoxCodeBorder: string;
  infoBoxCodeText: string;
}

/**
 * All themed color tokens the ProxiesManager UI needs, keyed by their usage.
 */
export const getProxiesThemeColors = (isDark: boolean): ProxiesThemeColors => ({
  containerBg: isDark ? '#1D293D' : '#F8FAFC',
  mainBg: isDark ? '#0F172B' : '#FFFFFF',
  cardBorder: isDark ? '#1E293B' : '#E2E8F0',
  titleColor: isDark ? '#E2E8F0' : '#314158',
  subtitleColor: isDark ? '#90A1B9' : '#62748E',
  tableHeaderBg: isDark ? '#1D293D' : '#F8FAFC',
  tableBorderColor: isDark ? '#1E293B' : '#E2E8F0',
  inputBg: isDark ? '#1D293D' : '#FFFFFF',
  inputBorder: isDark ? '#314158' : '#D1D5DB',
  inputText: isDark ? '#E2E8F0' : '#1E293B',
  buttonBg: isDark ? '#1D293D' : '#F3F4F6',
  buttonText: isDark ? '#90A1B9' : '#374151',
  paginationBg: isDark ? '#1D293D' : '#FFFFFF',
  paginationBorder: isDark ? '#314158' : '#D1D5DB',
  paginationText: isDark ? '#90A1B9' : '#374151',
  modalBackdrop: isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)',
  modalBg: isDark ? '#1D293D' : '#FFFFFF',
  modalInfoBg: isDark ? '#111827' : '#F9FAFB',
  countryBadge: {
    background: isDark ? '#1D293D' : '#F3F4F6',
    color: isDark ? '#E2E8F0' : '#374151',
  },
  providerBadge: {
    background: isDark ? '#1A0D2E' : '#F3E8FF',
    color: isDark ? '#C084FC' : '#7C3AED',
    borderColor: isDark ? '#6D28D9' : '#DDD6FE',
  },
  filterChipSearch: {
    background: isDark ? '#0C1A2E' : '#DBEAFE',
    color: isDark ? '#60A5FA' : '#1D4ED8',
  },
  filterChipStatus: {
    background: isDark ? '#0D2818' : '#DCFCE7',
    color: isDark ? '#5EE9B5' : '#15803D',
  },
  filterChipCountry: {
    background: isDark ? '#1A0D2E' : '#F3E8FF',
    color: isDark ? '#C084FC' : '#7C3AED',
  },
  filterChipProvider: {
    background: isDark ? '#0C1A2E' : '#E0E7FF',
    color: isDark ? '#818CF8' : '#4338CA',
  },
  warningBoxBg: isDark ? '#1C1507' : '#FFFBEB',
  warningBoxBorder: isDark ? '#854D0E' : '#FDE68A',
  warningBoxTitle: isDark ? '#FBBF24' : '#92400E',
  warningBoxText: isDark ? '#FCD34D' : '#B45309',
  warningBoxIcon: isDark ? '#FBBF24' : '#D97706',
  infoBoxBg: isDark ? '#0C1A2E' : '#EFF6FF',
  infoBoxBorder: isDark ? '#1E40AF' : '#BFDBFE',
  infoBoxTitle: isDark ? '#60A5FA' : '#1E3A8A',
  infoBoxText: isDark ? '#93C5FD' : '#1D4ED8',
  infoBoxIcon: isDark ? '#60A5FA' : '#2563EB',
  infoBoxCodeBg: isDark ? '#111827' : '#FFFFFF',
  infoBoxCodeBorder: isDark ? '#1E40AF' : '#BFDBFE',
  infoBoxCodeText: isDark ? '#E2E8F0' : '#374151',
});
